import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect } from 'react';
import { apiFetch } from '@/utils/api';
import { useI18n } from '@/hooks/useI18n';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import PaginationFooter from "@/components/PaginationFooter";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/switch";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import {
    BriefcaseIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    FunnelIcon
} from '@heroicons/react/24/outline';

interface SmallScheduler { id: string; name: string }

type TaxationType = 'full_time_employee' | 'daily_employee' | 'student';

interface TimesheetJobLevelRate {
    id: string;
    organization_id: string;
    job_level_id: string;
    taxation_type: TaxationType;
    hourly_rate: number | null;
    tip_amount: number | null;
    created_at: string;
    updated_at?: string;
}

interface TimesheetJobLevel {
    id: string;
    organization_id: string;
    job_id: string;
    level_name: 'Junior' | 'Experienced' | 'Senior';
    hourly_rate: number;
    tip_amount: number;
    is_default: boolean;
    sort_order: number;
    is_active: boolean;
    deactivated_at?: string;
    created_at: string;
    updated_at?: string;
    rates?: TimesheetJobLevelRate[];
}

interface TimesheetJob {
    id: string;
    organization_id: string;
    title: string;
    code?: string;
    color?: string;
    description?: string;
    gps_address?: string;
    gps_latitude?: number;
    gps_longitude?: number;
    connecteam_job_id?: string;
    is_deleted: boolean;
    is_connecteam_synced: boolean;
    scheduler_ids?: string[];
    schedulers?: SmallScheduler[];
    levels?: TimesheetJobLevel[];
    created_at: string;
    updated_at?: string;
}

interface JobListResponse {
    jobs: TimesheetJob[];
    total: number;
    page: number;
    per_page: number;
}

interface JobFormData {
    title: string;
    code: string;
    color: string;
    description: string;
    gps_address: string;
    gps_latitude: string;
    gps_longitude: string;
}

interface JobLevelRateFormData {
    taxation_type: TaxationType;
    hourly_rate: string;
    tip_amount: string;
}

interface JobLevelFormData {
    level_name: 'Junior' | 'Experienced' | 'Senior';
    is_default: boolean;
    sort_order: number;
    rates: JobLevelRateFormData[];
}


// Preset color swatches for quick selection
const COLOR_SWATCHES: string[] = [
  '#FF6F61', '#FFC107', '#43A047', '#1E88E5', '#8E24AA',
  '#E91E63', '#5E35B1', '#3949AB', '#00BCD4', '#8BC34A'
];

function ColorSwatchPicker({ value, onChange }: { value?: string; onChange: (hex: string) => void }) {
  const [open, setOpen] = useState(false);
  const current = value || '#9CA3AF'; // default gray if none selected
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Select color"
          className="h-8 w-8 rounded-full border shadow-sm"
          style={{ backgroundColor: current }}
        />
      </PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-auto p-3">
        <div className="grid grid-cols-5 gap-3">
          {COLOR_SWATCHES.map((hex) => (
            <button
              key={hex}
              type="button"
              aria-label={hex}
              className={`h-8 w-8 rounded-full border ${value === hex ? 'ring-2 ring-offset-2 ring-blue-500' : ''}`}
              style={{ backgroundColor: hex }}
              onClick={() => { onChange(hex); setOpen(false); }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Job Levels Editor Component with Taxation Type Matrix
function JobLevelsEditor({
    levels,
    onChange,
    disabled = false
}: {
    levels: JobLevelFormData[];
    onChange: (levels: JobLevelFormData[]) => void;
    disabled?: boolean;
}) {
    const { t } = useI18n();
    const LEVEL_OPTIONS: Array<'Junior' | 'Experienced' | 'Senior'> = ['Junior', 'Experienced', 'Senior'];
    const TAXATION_TYPES: TaxationType[] = ['full_time_employee', 'daily_employee', 'student'];
    const TAXATION_LABELS: Record<TaxationType, string> = {
        'full_time_employee': 'Full Time',
        'daily_employee': 'Daily',
        'student': 'Student'
    };

    const addLevel = () => {
        // Find first unused level name
        const usedLevels = new Set(levels.map(l => l.level_name));
        const availableLevel = LEVEL_OPTIONS.find(opt => !usedLevels.has(opt)) || 'Experienced';

        // Initialize with empty rates for all taxation types
        const newLevel: JobLevelFormData = {
            level_name: availableLevel,
            is_default: levels.length === 0,
            sort_order: levels.length + 1,
            rates: [
                { taxation_type: 'full_time_employee', hourly_rate: '', tip_amount: '' },
                { taxation_type: 'daily_employee', hourly_rate: '', tip_amount: '' },
                { taxation_type: 'student', hourly_rate: '', tip_amount: '' }
            ]
        };
        onChange([...levels, newLevel]);
    };

    const removeLevel = (index: number) => {
        if (levels.length === 1) {
            alert(t('employees.jobs.levels.atLeastOne', 'admin') || 'At least one level is required');
            return;
        }
        const newLevels = levels.filter((_, i) => i !== index);
        if (levels[index].is_default && newLevels.length > 0) {
            newLevels[0].is_default = true;
        }
        onChange(newLevels);
    };

    const updateLevel = (index: number, field: keyof JobLevelFormData, value: any) => {
        const newLevels = [...levels];

        if (field === 'level_name') {
            const isDuplicate = newLevels.some((level, i) => i !== index && level.level_name === value);
            if (isDuplicate) {
                alert(t('employees.jobs.levels.duplicateLevel', 'admin') ||
                      'Each level (Junior, Experienced, Senior) can only be used once.');
                return;
            }
        }

        newLevels[index] = { ...newLevels[index], [field]: value };

        if (field === 'is_default' && value === true) {
            newLevels.forEach((level, i) => {
                if (i !== index) level.is_default = false;
            });
        }

        onChange(newLevels);
    };

    const updateRate = (levelIndex: number, taxationType: TaxationType, field: 'hourly_rate' | 'tip_amount', value: string) => {
        const newLevels = [...levels];
        const level = newLevels[levelIndex];

        // Find or create rate for this taxation type
        let rate = level.rates.find(r => r.taxation_type === taxationType);
        if (!rate) {
            rate = { taxation_type: taxationType, hourly_rate: '', tip_amount: '' };
            level.rates.push(rate);
        }

        // Update the field
        rate[field] = value;

        onChange(newLevels);
    };

    const getRate = (level: JobLevelFormData, taxationType: TaxationType, field: 'hourly_rate' | 'tip_amount'): string => {
        const rate = level.rates?.find(r => r.taxation_type === taxationType);
        if (rate && rate[field]) {
            return rate[field];
        }
        // Fallback to full_time_employee rate
        const fullTimeRate = level.rates?.find(r => r.taxation_type === 'full_time_employee');
        if (fullTimeRate && fullTimeRate[field]) {
            return fullTimeRate[field];
        }
        return '';
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <Label>{t('employees.jobs.jobDialog.title', 'admin') || 'Job Levels & Hourly Rates by Taxation Type'}</Label>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLevel}
                    disabled={disabled || levels.length >= 3}
                    title={levels.length >= 3 ? 'All levels (Junior, Experienced, Senior) are already added' : ''}
                >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {t('employees.jobs.jobDialog.addLevel', 'admin') || 'Add Level'}
                </Button>
            </div>

            <div className="border  overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[120px]">{t('employees.jobs.jobDialog.levelName', 'admin') || 'Level'}</TableHead>
                            {TAXATION_TYPES.map(taxType => (
                                <TableHead key={taxType} colSpan={2} className="text-center border-l">
                                    {TAXATION_LABELS[taxType]}
                                </TableHead>
                            ))}
                            <TableHead className="w-[80px] border-l text-center">{t('employees.jobs.jobDialog.default', 'admin') || 'Default'}</TableHead>
                            <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                        <TableRow>
                            <TableHead></TableHead>
                            {TAXATION_TYPES.map(taxType => (
                                <React.Fragment key={taxType}>
                                    <TableHead className="w-[100px] text-xs border-l">Rate</TableHead>
                                    <TableHead className="w-[100px] text-xs">Tip</TableHead>
                                </React.Fragment>
                            ))}
                            <TableHead className="border-l"></TableHead>
                            <TableHead></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {levels.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} className="text-center text-muted-foreground py-4">
                                    {t('employees.jobs.jobDialog.noLevels', 'admin') || 'No levels defined. Click "Add Level" to create one.'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            levels.map((level, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Select
                                            value={level.level_name}
                                            onValueChange={(value) => updateLevel(index, 'level_name', value)}
                                            disabled={disabled}
                                        >
                                            <SelectTrigger className="w-[110px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {LEVEL_OPTIONS.map((option) => (
                                                    <SelectItem key={option} value={option}>
                                                        {option}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    {TAXATION_TYPES.map(taxType => (
                                        <React.Fragment key={taxType}>
                                            <TableCell className="border-l">
                                                <Input
                                                    type="number"
                                                    step="1"
                                                    min="1"
                                                    value={Math.round(parseFloat(getRate(level, taxType, 'hourly_rate')) || 0)}
                                                    onChange={(e) => updateRate(index, taxType, 'hourly_rate', e.target.value)}
                                                    disabled={disabled}
                                                    placeholder=""
                                                    className="w-[90px] text-right"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    value={Math.round(parseFloat(getRate(level, taxType, 'tip_amount')) || 0)}
                                                    onChange={(e) => updateRate(index, taxType, 'tip_amount', e.target.value)}
                                                    disabled={disabled}
                                                    placeholder=""
                                                    className="w-[90px] text-right"
                                                />
                                            </TableCell>
                                        </React.Fragment>
                                    ))}
                                    <TableCell className="border-l text-center">
                                        <Checkbox
                                            checked={level.is_default}
                                            onCheckedChange={(checked) => updateLevel(index, 'is_default', checked)}
                                            disabled={disabled}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeLevel(index)}
                                            disabled={disabled || levels.length === 1}
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

export default function JobsTab() {
    const { t } = useI18n();
    const [loading, setLoading] = useState(true);
    const [jobs, setJobs] = useState<TimesheetJob[]>([]);
    const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0, limit: 20 });
    const [error, setError] = useState<string | null>(null);

    // Filter states
    const [search, setSearch] = useState('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [filterType, setFilterType] = useState<'all' | 'connecteam' | 'manual'>('all');

    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Form states
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [editingJob, setEditingJob] = useState<TimesheetJob | null>(null);
    const [isEditingConnecteam, setIsEditingConnecteam] = useState(false);
    const [formData, setFormData] = useState<JobFormData>({
        title: '',
        code: '',
        color: '',
        description: '',
        gps_address: '',
        gps_latitude: '',
        gps_longitude: ''
    });

    // Scheduler options
    const [schedulerOptions, setSchedulerOptions] = useState<SmallScheduler[]>([]);
    const [selectedSchedulers, setSelectedSchedulers] = useState<string[]>([]);
    const [formLoading, setFormLoading] = useState(false);

    // Job levels state
    const [jobLevels, setJobLevels] = useState<JobLevelFormData[]>([
        {
            level_name: 'Experienced',
            is_default: true,
            sort_order: 1,
            rates: [
                { taxation_type: 'full_time_employee', hourly_rate: '', tip_amount: '' },
                { taxation_type: 'daily_employee', hourly_rate: '', tip_amount: '' },
                { taxation_type: 'student', hourly_rate: '', tip_amount: '' }
            ]
        }
    ]);



    useEffect(() => {
        fetchJobs();
    }, [pagination.page, pagination.limit, search, includeDeleted, filterType]);
    // Load scheduler options (on mount and when dialogs open if empty)
    const loadSchedulers = async () => {
        try {
            const resp = await apiFetch(`/api/timesheet/schedulers?per_page=100&include_archived=false`);
            if (!resp.ok) {
                console.error('Failed to load schedulers: HTTP', resp.status);
                return;
            }
            const data = await resp.json();
            const opts = (data.schedulers || []).map((s: any) => ({ id: s.id, name: s.name }));
            setSchedulerOptions(opts);
        } catch (e) {
            console.error('Failed to load schedulers', e);
        }
    };

    useEffect(() => { loadSchedulers(); }, []);

    // Reset to first page when filters change
    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [search, includeDeleted, filterType]);

    // When opening create/edit dialog, ensure scheduler options are loaded
    useEffect(() => {
        if ((showCreateDialog || editingJob) && schedulerOptions.length === 0) {
            loadSchedulers();
        }
    }, [showCreateDialog, editingJob]);


    const fetchJobs = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                per_page: pagination.limit.toString(),
                include_deleted: includeDeleted.toString(),
            });

            if (search) params.append('search', search);
            if (filterType === 'connecteam') params.append('connecteam_only', 'true');
            if (filterType === 'manual') params.append('manual_only', 'true');

            const response = await apiFetch(`/api/timesheet/jobs?${params.toString()}`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: JobListResponse = await response.json();
            setJobs(data.jobs);
            setPagination(prev => ({
                ...prev,
                total: data.total,
                pages: Math.ceil((data.total || 0) / (data.per_page || prev.limit)),
                page: data.page || prev.page,
                limit: data.per_page || prev.limit,
            }));
        } catch (err: any) {
            console.error('Failed to fetch jobs:', err);
            setError(err.message || 'Failed to load jobs');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            code: '',
            color: '',
            description: '',
            gps_address: '',
            gps_latitude: '',
            gps_longitude: ''
        });
        setSelectedSchedulers([]);
        setEditingJob(null);
        setIsEditingConnecteam(false);
        setJobLevels([{
            level_name: 'Experienced',
            is_default: true,
            sort_order: 1,
            rates: [
                { taxation_type: 'full_time_employee', hourly_rate: '', tip_amount: '' },
                { taxation_type: 'daily_employee', hourly_rate: '', tip_amount: '' },
                { taxation_type: 'student', hourly_rate: '', tip_amount: '' }
            ]
        }]);
    };

    const handleCreateJob = async () => {
        // Validate levels before submitting - check that at least full_time_employee rate is set
        const invalidLevels = jobLevels.filter(level => {
            const fullTimeRate = level.rates?.find(r => r.taxation_type === 'full_time_employee');
            return !fullTimeRate || parseFloat(fullTimeRate.hourly_rate || '0') <= 0;
        });
        if (invalidLevels.length > 0) {
            setError('All levels must have a full-time employee hourly rate greater than 0');
            return;
        }

        // Check for duplicate level names
        const levelNames = jobLevels.map(l => l.level_name);
        if (levelNames.length !== new Set(levelNames).size) {
            setError('Each level (Junior, Experienced, Senior) can only be used once');
            return;
        }

        setFormLoading(true);
        try {
            const payload = {
                title: formData.title,
                code: formData.code || null,
                color: formData.color || null,
                description: formData.description || null,
                gps_address: formData.gps_address ? formData.gps_address : null,
                gps_latitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : null,
                gps_longitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : null,
                scheduler_ids: selectedSchedulers,
                levels: jobLevels.map(level => {
                    // Get full-time rate as fallback
                    const fullTimeRate = level.rates?.find(r => r.taxation_type === 'full_time_employee');
                    const fallbackHourlyRate = fullTimeRate?.hourly_rate || '0';
                    const fallbackTipAmount = fullTimeRate?.tip_amount || '0';

                    return {
                        level_name: level.level_name,
                        is_default: level.is_default,
                        sort_order: level.sort_order,
                        rates: level.rates?.map(rate => ({
                            taxation_type: rate.taxation_type,
                            hourly_rate: parseFloat(rate.hourly_rate || fallbackHourlyRate),
                            tip_amount: parseFloat(rate.tip_amount || fallbackTipAmount)
                        }))
                    };
                })
            };


            const response = await apiFetch('/api/timesheet/jobs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create job');
            }

            setShowCreateDialog(false);
            resetForm();
            fetchJobs();
        } catch (err: any) {
            console.error('Failed to create job:', err);
            setError(err.message);
        } finally {
            setFormLoading(false);
        }
    };

    const handleUpdateJob = async () => {
        if (!editingJob) return;

        // Validate levels before submitting - check that at least full_time_employee rate is set
        const invalidLevels = jobLevels.filter(level => {
            const fullTimeRate = level.rates?.find(r => r.taxation_type === 'full_time_employee');
            return !fullTimeRate || parseFloat(fullTimeRate.hourly_rate || '0') <= 0;
        });
        if (invalidLevels.length > 0) {
            setError('All levels must have a full-time employee hourly rate greater than 0');
            return;
        }

        // Check for duplicate level names
        const levelNames = jobLevels.map(l => l.level_name);
        if (levelNames.length !== new Set(levelNames).size) {
            setError('Each level (Junior, Experienced, Senior) can only be used once');
            return;
        }

        setFormLoading(true);
        try {
            // For Connecteam jobs, only send levels
            const payload = isEditingConnecteam ? {
                levels: jobLevels.map(level => {
                    // Get full-time rate as fallback
                    const fullTimeRate = level.rates?.find(r => r.taxation_type === 'full_time_employee');
                    const fallbackHourlyRate = fullTimeRate?.hourly_rate || '0';
                    const fallbackTipAmount = fullTimeRate?.tip_amount || '0';

                    return {
                        level_name: level.level_name,
                        is_default: level.is_default,
                        sort_order: level.sort_order,
                        rates: level.rates?.map(rate => ({
                            taxation_type: rate.taxation_type,
                            hourly_rate: parseFloat(rate.hourly_rate || fallbackHourlyRate),
                            tip_amount: parseFloat(rate.tip_amount || fallbackTipAmount)
                        }))
                    };
                })
            } : {
                title: formData.title,
                code: formData.code || null,
                color: formData.color || null,
                description: formData.description || null,
                gps_address: formData.gps_address ? formData.gps_address : null,
                gps_latitude: formData.gps_latitude ? parseFloat(formData.gps_latitude) : null,
                gps_longitude: formData.gps_longitude ? parseFloat(formData.gps_longitude) : null,
                scheduler_ids: selectedSchedulers,
                levels: jobLevels.map(level => {
                    // Get full-time rate as fallback
                    const fullTimeRate = level.rates?.find(r => r.taxation_type === 'full_time_employee');
                    const fallbackHourlyRate = fullTimeRate?.hourly_rate || '0';
                    const fallbackTipAmount = fullTimeRate?.tip_amount || '0';

                    return {
                        level_name: level.level_name,
                        is_default: level.is_default,
                        sort_order: level.sort_order,
                        rates: level.rates?.map(rate => ({
                            taxation_type: rate.taxation_type,
                            hourly_rate: parseFloat(rate.hourly_rate || fallbackHourlyRate),
                            tip_amount: parseFloat(rate.tip_amount || fallbackTipAmount)
                        }))
                    };
                })
            };


            const response = await apiFetch(`/api/timesheet/jobs/${editingJob.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update job');
            }

            // Close dialog immediately, then refresh
            setEditingJob(null);
            resetForm();

            // Fetch jobs in background
            fetchJobs();
        } catch (err: any) {
            console.error('Failed to update job:', err);
            setError(err.message);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteJob = async (job: TimesheetJob) => {
        if (!confirm(`Are you sure you want to delete "${job.title}"?`)) return;

        try {
            const response = await apiFetch(`/api/timesheet/jobs/${job.id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to delete job');
            }

            fetchJobs();
        } catch (err: any) {
            console.error('Failed to delete job:', err);
            setError(err.message);
        }
    };

    const openEditDialog = (job: TimesheetJob) => {
        // Batch all state updates using React 18's automatic batching
        // Set all state BEFORE opening the dialog to prevent flash
        const newFormData = {
            title: job.title,
            code: job.code || '',
            color: job.color || '',
            description: job.description || '',
            gps_address: job.gps_address || '',
            gps_latitude: job.gps_latitude?.toString() || '',
            gps_longitude: job.gps_longitude?.toString() || ''
        };

        const newSchedulers = (job.scheduler_ids || []).slice();

        const newLevels = (job.levels && job.levels.length > 0)
            ? job.levels.map(level => ({
                level_name: level.level_name,
                is_default: level.is_default,
                sort_order: level.sort_order,
                rates: level.rates?.map(rate => ({
                    taxation_type: rate.taxation_type as TaxationType,
                    hourly_rate: rate.hourly_rate?.toString() || '',
                    tip_amount: rate.tip_amount?.toString() || ''
                })) || []
            }))
            : [{
                level_name: 'Experienced' as const,
                is_default: true,
                sort_order: 1,
                rates: [
                    { taxation_type: 'full_time_employee' as TaxationType, hourly_rate: '', tip_amount: '' },
                    { taxation_type: 'daily_employee' as TaxationType, hourly_rate: '', tip_amount: '' },
                    { taxation_type: 'student' as TaxationType, hourly_rate: '', tip_amount: '' }
                ]
            }];

        // Update all state in one batch
        setIsEditingConnecteam(job.is_connecteam_synced || false);
        setFormData(newFormData);
        setSelectedSchedulers(newSchedulers);
        setJobLevels(newLevels);

        // Open dialog LAST after all data is ready
        setEditingJob(job);
    };

    const formatJobBadge = (job: TimesheetJob) => {
        if (job.is_connecteam_synced) {
            return <Badge variant="secondary">Connecteam</Badge>;
        }
        return <Badge variant="outline">Manual</Badge>;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                        <BriefcaseIcon className="h-8 w-8" />
                        {t('navigation.jobs', 'common')}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {t('employees.jobs.subtitle', 'admin')}
                    </p>
                </div>
                <Button onClick={() => setShowCreateDialog(true)}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    {t('actions.addNew', 'common')} {t('employees.jobs.job', 'admin')}
                </Button>
            </div>

            {/* Filters */}
            <div className="flex gap-4 items-center flex-wrap">
                <div className="relative flex-1 min-w-64">
                    <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search jobs..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                    />
                </div>
                <Select value={filterType} onValueChange={(value: any) => setFilterType(value)}>
                    <SelectTrigger className="w-40">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('actions.all', 'common')}</SelectItem>
                        <SelectItem value="connecteam">{t('employees.jobs.connecteamOnly', 'admin')}</SelectItem>
                        <SelectItem value="manual">{t('employees.jobs.manualOnly', 'admin')}</SelectItem>
                    </SelectContent>
                </Select>
                <Button
                    variant="outline"
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                >
                    <FunnelIcon className="h-4 w-4 mr-2" />
                    {t('actions.advancedFilters', 'common')}
                </Button>
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && (
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex items-center space-x-2">
                            <Switch
                                checked={includeDeleted}
                                onChange={setIncludeDeleted}
                                className=""
                            />
                            <Label htmlFor="include-deleted"> {t('employees.jobs.includeDeleted', 'admin')}</Label>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Error State */}
            {error && (
                <Card className="border-destructive">
                    <CardContent className="pt-6">
                        <p className="text-destructive">{error}</p>
                    </CardContent>
                </Card>
            )}



            {/* Jobs Table */}
            {loading ? (<Card>
                <CardContent className="p-0">

                    <div className="p-6 space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-full" />
                    </div>
                </CardContent>
            </Card>
            ) : (
                <div className="bg-white">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('employees.jobs.job', 'admin')}</TableHead>
                                <TableHead>{t('employees.jobs.code', 'admin')}</TableHead>
                                <TableHead>{t('employees.jobs.type', 'admin')}</TableHead>
                                <TableHead>{t('employees.jobs.description', 'admin')}</TableHead>
                                <TableHead>{t('employees.jobs.address', 'admin')}</TableHead>
                                <TableHead>{t('employees.jobs.schedulers', 'admin')}</TableHead>
                                <TableHead>{t('actions.actions', 'common')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {jobs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">
                                        <BriefcaseIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                        <p className="text-muted-foreground">{t('employees.jobs.noJobsFound', 'admin')}</p>
                                        <p className="text-sm text-muted-foreground mt-2">
                                            {t('employees.jobs.createYourFirstJob', 'admin')}
                                        </p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                jobs.map((job) => (
                                    <TableRow key={job.id} className={job.is_deleted ? 'opacity-50' : ''}>
                                        <TableCell className="font-medium flex items-center gap-2">
                                            {job.color && (
                                                <div
                                                    className="w-4 h-4 rounded border"
                                                    style={{ backgroundColor: job.color }}
                                                />
                                            )}
                                            {job.title}
                                            {job.is_deleted && <span className="text-muted-foreground ml-2">({t('status.deleted', 'admin')})</span>}
                                        </TableCell>
                                        <TableCell>{job.code || '-'}</TableCell>
                                        <TableCell>{formatJobBadge(job)}</TableCell>
                                        <TableCell className="max-w-xs truncate">
                                            {job.description || '-'}
                                        </TableCell>
                                        <TableCell className="max-w-xs truncate">
                                            {job.gps_address || '-'}
                                        </TableCell>
                                        <TableCell className="max-w-xs">
                                            <div className="flex flex-wrap gap-1">
                                                {(job.schedulers || []).map((s) => (
                                                    <Badge key={s.id} variant="outline">{s.name}</Badge>
                                                ))}
                                                {(!job.schedulers || job.schedulers.length === 0) && (
                                                    <span className="text-muted-foreground">-</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                {!job.is_deleted && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openEditDialog(job)}
                                                        >
                                                            <PencilIcon className="h-4 w-4" />
                                                        </Button>
                                                    </>
                                                )}
                                                {!job.is_connecteam_synced && !job.is_deleted && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeleteJob(job)}
                                                            className="text-destructive hover:text-destructive"
                                                        >
                                                            <TrashIcon className="h-4 w-4" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Pagination */}
            <PaginationFooter
                pagination={pagination}
                onPageChange={(p) => setPagination(prev => ({ ...prev, page: p }))}
                onPageSizeChange={(size) => setPagination(prev => ({ ...prev, limit: size, page: 1 }))}
            />

            {/* Create Job Dialog */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent className="sm:max-w-7xl" onInteractOutside={(e) => { const t = e.target as HTMLElement; const pac = document.querySelector('.pac-container'); if (pac && t && pac.contains(t)) { e.preventDefault(); } }}>
                    <DialogHeader>
                        <DialogTitle>{t('employees.jobs.jobDialog.createTitle', 'admin')}</DialogTitle>
                        <DialogDescription>
                            {t('employees.jobs.jobDialog.createDescription', 'admin')}

                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="title">{t('employees.jobs.jobDialog.title', 'admin')} *</Label>
                            <Input
                                id="title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="e.g., Barista, Manager"
                            />
                        </div>
                        <div className="flex gap-4">
                          <div className='w-80 sm-w-64'>
                            <Label htmlFor="code">{t('employees.jobs.jobDialog.code', 'admin')}</Label>
                            <Input
                              id="code"
                              value={formData.code}
                              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                              placeholder="e.g., BAR, MGR"
                            />
                          </div>
                          <div>
                            <ColorSwatchPicker
                              value={formData.color}
                              onChange={(hex) => setFormData({ ...formData, color: hex })}
                            />
                          </div>
                        </div>
                        <div>
                            <Label htmlFor="description">{t('employees.jobs.jobDialog.description', 'admin')}</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Job description..."
                            />
                        </div>
                        <div>
                            <Label htmlFor="address">{t('employees.jobs.jobDialog.address', 'admin')}</Label>
                            <GooglePlacesAutocomplete
                                value={formData.gps_address}
                                onAddressChange={(val: string) => setFormData(prev => ({ ...prev, gps_address: val }))}
                                onPlaceSelected={(addr: string, lat: number | null, lng: number | null) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        gps_address: addr,
                                        gps_latitude: lat !== null ? String(lat) : '',
                                        gps_longitude: lng !== null ? String(lng) : '',
                                    }))
                                }}
                            />
                        </div>
                        <div>
                            <Label>{t('employees.jobs.jobDialog.schedulers', 'admin')}</Label>
                            <div className="mt-2 max-h-40 overflow-auto border rounded p-2">
                                {schedulerOptions.length === 0 && (
                                    <div className="text-sm text-muted-foreground">{t('employees.jobs.jobDialog.noSchedulersAvailable', 'admin')}</div>
                                )}
                                {schedulerOptions.map((s) => (
                                    <label key={s.id} className="flex items-center gap-2 py-1">
                                        <Checkbox
                                            checked={selectedSchedulers.includes(s.id)}
                                            onCheckedChange={(checked: boolean) => {
                                                setSelectedSchedulers((prev) =>
                                                    checked ? [...prev, s.id] : prev.filter((id) => id !== s.id)
                                                );
                                            }}
                                        />
                                        <span>{s.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Job Levels Editor */}
                        <JobLevelsEditor
                            levels={jobLevels}
                            onChange={setJobLevels}
                        />

                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                            {t('actions.cancel', 'common')}
                        </Button>
                        <Button onClick={handleCreateJob} disabled={formLoading || !formData.title}>
                            {formLoading ? 'Creating...' : t('actions.save', 'common')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Job Dialog */}
            <Dialog open={!!editingJob} onOpenChange={() => { setEditingJob(null); resetForm(); }}>
                <DialogContent
                    key={editingJob?.id}
                    className="sm:max-w-6xl transition-all duration-50"
                    onInteractOutside={(e) => { const t = e.target as HTMLElement; const pac = document.querySelector('.pac-container'); if (pac && t && pac.contains(t)) { e.preventDefault(); } }}
                >
                    <DialogHeader>
                        <DialogTitle>{t('employees.jobs.jobDialog.editTitle', 'admin')}</DialogTitle>
                        <DialogDescription>
                            {isEditingConnecteam ? (
                                <span className="text-amber-600">
                                    {t('employees.jobs.jobDialog.connecteamWarning', 'admin') ||
                                     'This job is synced from Connecteam. Only hourly rates can be edited here.'}
                                </span>
                            ) : (
                                t('employees.jobs.jobDialog.editDescription', 'admin')
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {!isEditingConnecteam ? (
                            <>
                                <div>
                                    <Label htmlFor="edit-title">{t('employees.jobs.jobDialog.title', 'admin')} *</Label>
                                    <Input
                                        id="edit-title"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        placeholder="e.g., Barista, Manager"
                                    />
                                </div>
                                <div className="flex gap-4">
                                  <div className='w-80 sm-w-64'>
                                    <Label htmlFor="edit-code">{t('employees.jobs.jobDialog.code', 'admin')}</Label>
                                    <Input
                                      id="edit-code"
                                      value={formData.code}
                                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                      placeholder="e.g., BAR, MGR"
                                    />
                                  </div>
                                  <div className="pt-4">
                                    <ColorSwatchPicker
                                      value={formData.color}
                                      onChange={(hex) => setFormData({ ...formData, color: hex })}
                                    />
                                  </div>
                                </div>
                                <div>
                                    <Label htmlFor="edit-description">{t('employees.jobs.jobDialog.description', 'admin')}</Label>
                                    <Textarea
                                        id="edit-description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Job description..."
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="edit-address">{t('employees.jobs.jobDialog.address', 'admin')}</Label>
                                    <GooglePlacesAutocomplete
                                        value={formData.gps_address}
                                        onAddressChange={(val: string) => setFormData(prev => ({ ...prev, gps_address: val }))}
                                        onPlaceSelected={(addr: string, lat: number | null, lng: number | null) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                gps_address: addr,
                                                gps_latitude: lat !== null ? String(lat) : '',
                                                gps_longitude: lng !== null ? String(lng) : '',
                                            }))
                                        }}
                                    />
                                </div>
                                <div>
                                    <Label>{t('employees.jobs.jobDialog.schedulers', 'admin')}</Label>
                                    <div className="mt-2 max-h-40 overflow-auto border rounded p-2">
                                        {schedulerOptions.length === 0 && (
                                            <div className="text-sm text-muted-foreground">{t('employees.jobs.jobDialog.noSchedulersAvailable', 'admin')}</div>
                                        )}
                                        {schedulerOptions.map((s) => (
                                            <label key={s.id} className="flex items-center gap-2 py-1">
                                                <Checkbox
                                                    checked={selectedSchedulers.includes(s.id)}
                                                    onCheckedChange={(checked: boolean) => {
                                                        setSelectedSchedulers((prev) =>
                                                            checked ? [...prev, s.id] : prev.filter((id) => id !== s.id)
                                                        );
                                                    }}
                                                />
                                                <span>{s.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                                <p className="font-medium text-amber-900">{editingJob?.title}</p>

                            </div>
                        )}

                        {/* Job Levels Editor - always shown */}
                        <JobLevelsEditor
                            levels={jobLevels}
                            onChange={setJobLevels}
                        />

                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setEditingJob(null); resetForm(); }} disabled={formLoading}>
                            {t('actions.cancel', 'common')}
                        </Button>
                        <Button
                            onClick={handleUpdateJob}
                            disabled={formLoading || (!isEditingConnecteam && !formData.title)}
                        >
                            {formLoading ? 'Updating...' : 'Update Job'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
